/**
 * Term Theme Registry — one theme per (school year level, NZ school term).
 *
 * Each theme has:
 *   - term: 1-4
 *   - available: true once the art assets ship; false = "coming soon"
 *   - name + teReo title + short description
 *   - assetPrefix: public path to scene_N.png assets (scene_0 = empty)
 *   - accent: Tailwind colour token for cards/buttons (fern | moana | kowhai | pohutukawa)
 *   - emptyCopy + tagline: shown when 0 active days
 *   - shareSubtitle: used when the whānau shares the scene
 *   - milestones: 7 active-day thresholds with unlock metadata.
 *
 * Metric: ACTIVE DAYS — distinct dates on which ≥1 lesson in that
 * (year, term)'s curriculum was completed. Fair across children who do 1 vs
 * 2 subjects and rewards consistency over intensity. Year 6 has its own
 * theme set (a "journey" narrative distinct from Year 5's static seasonal
 * scenes — see docs/curriculum/year6-theme-briefs.md) so progress never
 * merges across year levels.
 */

// Shared milestone thresholds (days 1 / 3 / 7 / 14 / 22 / 30 / 40).
// Copy/unlock labels differ per theme.
const MILESTONES_T1 = [
  { count: 1,  key: 'harakeke',   label: 'Harakeke shoot',         teReo: 'Te tipu o te harakeke',  scene: 1,
    note: 'Harakeke (NZ flax) isn\'t a true flax at all — it\'s in the lily family. Māori wove its long leaves into baskets, mats and rope (muka), and every plant traditionally had its outer leaves left uncut to protect the young shoots at the centre.' },
  { count: 3,  key: 'kowhai',     label: 'Kōwhai sapling',         teReo: 'Te kōwhai e tipu ana',   scene: 2,
    note: 'Kōwhai is one of NZ\'s best-known native trees, named for its bright yellow (kōwhai) flowers each spring. Tūī and other nectar-feeding birds time their feeding to the kōwhai\'s bloom.' },
  { count: 7,  key: 'fern',       label: 'Silver fern',            teReo: 'Te ponga',                scene: 3,
    note: 'The ponga (silver fern) has fronds that are green on top and silvery-white underneath — early Māori travellers reportedly turned the fronds over to reflect moonlight and mark trails at night.' },
  { count: 14, key: 'pohutukawa', label: 'Pōhutukawa tree',        teReo: 'Te pōhutukawa',           scene: 4,
    note: 'Pōhutukawa is called the "New Zealand Christmas tree" for its brilliant red flowers that bloom in December. It often grows on coastal cliffs, its roots gripping bare rock.' },
  { count: 22, key: 'tui',        label: 'Tūī arrives',            teReo: 'Kua tae mai te tūī',      scene: 5,
    note: 'Tūī have two voice boxes, letting them sing complex, layered songs — and even mimic other birds. Look for the small tuft of white feathers at their throat.' },
  { count: 30, key: 'kereru',     label: 'Kererū joins the canopy',teReo: 'Te kererū',               scene: 6,
    note: 'Kererū (NZ wood pigeon) are one of the only birds left large enough to swallow big native fruit like karaka and taraire whole — which makes them essential for spreading those trees\' seeds through the forest.' },
  { count: 40, key: 'flowering',  label: 'Garden in full bloom',   teReo: 'Te māra e puāwai ana',    scene: 7,
    note: 'A mature native garden like this supports a whole food web — nectar and fruit for birds, shelter for insects, and roots that hold the soil together. Every plant and bird in this scene would once have been common across Aotearoa before introduced predators and land clearing.' },
];

const MILESTONES_T2 = [
  { count: 1,  key: 'matariki',       label: 'Matariki — mother star',       teReo: 'Matariki — te whetū ariki',           scene: 1,
    note: 'Matariki is a cluster of stars (also known as the Pleiades) that disappears from the night sky in April/May and reappears low on the horizon in midwinter — its return marks the start of the Māori New Year.' },
  { count: 3,  key: 'tupuanuku',      label: 'Tupu-ā-nuku — food from the soil', teReo: 'Tupu-ā-nuku — ngā kai o te whenua', scene: 2,
    note: 'Tupu-ā-nuku is the Matariki star linked to everything that grows in the ground — kūmara, potatoes, and other root crops. Traditionally, its brightness was read as a sign of how the coming season\'s crops would fare.' },
  { count: 7,  key: 'tupurangi',      label: 'Tupu-ā-rangi — food from trees',   teReo: 'Tupu-ā-rangi — ngā kai o te rangi',  scene: 3,
    note: 'Tupu-ā-rangi represents food that comes from above the ground — fruit, berries, and birds. It\'s considered the sibling star to Tupu-ā-nuku, together covering everything the whenua (land) provides.' },
  { count: 14, key: 'waiti',          label: 'Waitī — fresh water',          teReo: 'Waitī — te wai māori',                scene: 4,
    note: 'Waitī is linked to fresh water — rivers, streams, and lakes — and the food sources found within them, like tuna (eels) and kōura (freshwater crayfish).' },
  { count: 22, key: 'waita',          label: 'Waitā — the ocean',            teReo: 'Waitā — te moana',                    scene: 5,
    note: 'Waitā connects to the ocean and the kaimoana (seafood) it provides. Its position in the sky was traditionally used to help predict conditions for the sea during the year ahead.' },
  { count: 30, key: 'waipunarangi',   label: 'Waipuna-ā-rangi — rain',       teReo: 'Waipuna-ā-rangi — te ua',             scene: 6,
    note: 'Waipuna-ā-rangi is associated with rain — its brightness was traditionally read as a forecast for how much rainfall the coming year would bring.' },
  { count: 40, key: 'ururangi',       label: 'Ururangi — full cluster',      teReo: 'Ururangi — te hau, kua rite',          scene: 7,
    note: 'Ururangi is the star of the winds. Matariki actually has nine stars in total (the ones honoured here are the best-known); the whole cluster\'s reappearance each midwinter is a time for reflection, remembrance, and looking ahead to the year to come.' },
];

const MILESTONES_T3 = [
  { count: 1,  key: 'patangaroa',  label: 'Pātangaroa (starfish)',       teReo: 'Te pātangaroa',      scene: 1,
    note: 'Starfish (pātangaroa) aren\'t fish at all — they have no brain or blood, move on hundreds of tiny tube feet, and can regrow a lost arm. Some species can even regrow a whole new body from just one arm.' },
  { count: 3,  key: 'kina',        label: 'Kina (sea urchin)',           teReo: 'Te kina',            scene: 2,
    note: 'Kina graze on seaweed using five sharp teeth arranged in a ring on their underside. A healthy population of their predators (like crayfish and snapper) keeps kina numbers in balance so kelp forests can thrive.' },
  { count: 7,  key: 'paua',        label: 'Pāua (abalone)',              teReo: 'Te pāua',            scene: 3,
    note: 'Pāua shells are famous for their iridescent blue-green colour, caused by microscopic layers of nacre reflecting light. A single pāua can take years to reach a legal harvesting size.' },
  { count: 14, key: 'papaka',      label: 'Papaka (crab)',               teReo: 'Te papaka',          scene: 4,
    note: 'Crabs (papaka) breathe using gills, but many rockpool species can survive out of water for surprisingly long stretches as long as their gills stay damp — letting them dodge the tide as it goes in and out.' },
  { count: 22, key: 'wheke',       label: 'Wheke (octopus)',             teReo: 'Te wheke',           scene: 5,
    note: 'Octopus (wheke) have three hearts and blue blood, and are considered one of the most intelligent invertebrates alive — able to solve puzzles, squeeze through gaps barely wider than their beak, and change both colour and skin texture in an instant.' },
  { count: 30, key: 'rimurimu',    label: 'Rimurimu (seaweed forest)',   teReo: 'Te rimurimu',        scene: 6,
    note: 'A rimurimu (seaweed/kelp) forest is a nursery for young fish and shelter for countless small creatures — much like a forest on land, it provides both food and cover for the whole ecosystem around it.' },
  { count: 40, key: 'aihe',        label: 'Aihe (dolphin pod passing)',  teReo: 'Te aihe',            scene: 7,
    note: 'Dolphins (aihe) travel in pods and use echolocation — clicking sounds that bounce off objects — to navigate and hunt, even in murky water. A pod passing close to shore is often a sign of a healthy, fish-rich coastline.' },
];

const MILESTONES_T4 = [
  { count: 1,  key: 'shell',        label: 'First shell',                  teReo: 'Te anga',                   scene: 1,
    note: 'Most beach shells are the old, empty exoskeletons of molluscs — the shell grows in a spiral, adding new material to the open edge as the animal inside grows larger.' },
  { count: 3,  key: 'driftwood',    label: 'Driftwood log',                teReo: 'Te rākau pakoko',           scene: 2,
    note: 'Driftwood can travel huge distances on ocean currents — a log that washes up on a NZ beach may have started its journey on a riverbank hundreds or even thousands of kilometres away.' },
  { count: 7,  key: 'pohutukawa',   label: 'Pōhutukawa budding',           teReo: 'Te pōhutukawa e pihi ana', scene: 3,
    note: 'Pōhutukawa buds start out tightly closed and silvery-green — the burst of red comes later, when thousands of tiny stamens (not petals) unfurl all at once.' },
  { count: 14, key: 'pohutukawa_bloom', label: 'Pōhutukawa in full red bloom', teReo: 'Te pōhutukawa e puāwai ana', scene: 4,
    note: 'Pōhutukawa is nicknamed the "New Zealand Christmas tree" because it blooms bright red every December — its flowering has long been used as a natural seasonal marker for summer\'s arrival.' },
  { count: 22, key: 'whanau',       label: 'Whānau picnic on the sand',    teReo: 'He hui whānau i te tātahi', scene: 5,
    note: 'Summer beach gatherings are a big part of Kiwi and Pacific culture — a chance for extended whānau to reconnect, share kai, and pass down stories and swimming/fishing knowledge to tamariki.' },
  { count: 30, key: 'tara',         label: 'Tara (terns) overhead',        teReo: 'Ngā tara',                  scene: 6,
    note: 'Terns (tara) are fast, agile seabirds that hover and dive to catch small fish near the surface — several native tara species nest on NZ beaches and are vulnerable to disturbance during breeding season.' },
  { count: 40, key: 'sunset',       label: 'Summer sunset — full scene',   teReo: 'Te tōnga o te rā o te raumati', scene: 7,
    note: 'A full summer beach scene brings every part of the coastal ecosystem together — dunes, driftwood, flowering pōhutukawa, whānau, and birdlife — all depending on the same stretch of coastline staying healthy.' },
];

const MILESTONES_Y6_T1 = [
  { count: 1,  key: 'trail_begins', label: 'Trail begins',           teReo: 'Ka tīmata te ara',          scene: 1,
    note: 'Most NZ mountain tracks start in lowland native forest before climbing through distinct zones — the plants and animals you\'ll see change completely as you gain altitude.' },
  { count: 3,  key: 'tawhai',       label: 'Tawhai forest',          teReo: 'Te ngahere tawhai',         scene: 2,
    note: 'Tawhai (beech) forest dominates much of NZ\'s mountain country. Its small, tough leaves cope well with cold, and its shallow, tangled root systems help stabilise steep slopes.' },
  { count: 7,  key: 'bushline',     label: 'Bushline',               teReo: 'Te rohe ngahere',           scene: 3,
    note: 'The bushline is the altitude where forest gives way to open alpine terrain — trees get shorter and more twisted as they near this limit, unable to survive the colder, windier conditions above it.' },
  { count: 14, key: 'tussock',      label: 'Tussock slopes',         teReo: 'Ngā tarutaru maunga',       scene: 4,
    note: 'Tussock grasslands cover huge areas above the NZ bushline. These tough, clumping grasses can survive frost, snow, and strong wind — and turn a golden colour that\'s especially vivid in autumn light.' },
  { count: 22, key: 'ridge',        label: 'Rocky ridge',            teReo: 'Te ara toka',               scene: 5,
    note: 'Alpine ridgelines are exposed to extreme weather and temperature swings. Plants up here are often tiny and cushion-shaped, growing low and dense to avoid wind and hold onto scarce warmth.' },
  { count: 30, key: 'near_summit',  label: 'Near the summit',        teReo: 'E tata ana ki te tihi',     scene: 6,
    note: 'Near a summit, kea — the world\'s only alpine parrot — are sometimes spotted. They\'re famously curious and intelligent, known for investigating (and occasionally dismantling) tramping gear left unattended.' },
  { count: 40, key: 'summit',       label: 'Summit — full scene',    teReo: 'Kua eke te tihi',           scene: 7,
    note: 'Reaching a summit means climbing through every zone this scene shows — forest, bushline, tussock, and bare rock — each one adapted to progressively harsher conditions the higher you go.' },
];

const MILESTONES_Y7_T1 = [
  { count: 1,  key: 'casting_off',       label: 'Casting off',                  teReo: 'Te wehenga atu',                    scene: 1,
    note: 'A waka hourua is a double-hulled voyaging canoe — the same style of vessel used by Polynesian navigators to deliberately settle Aotearoa and the wider Pacific, sailing thousands of kilometres of open ocean without instruments.' },
  { count: 3,  key: 'reading_matariki',  label: 'Reading Matariki',             teReo: 'Te tirotiro i a Matariki',          scene: 2,
    note: 'Traditional wayfinders used the rising and setting points of stars like Matariki, along with ocean swells, wind, and even the flight paths of birds, to hold a course without any instruments at all.' },
  { count: 7,  key: 'open_ocean',        label: 'Open ocean',                   teReo: 'Te moana nui',                      scene: 3,
    note: 'On open ocean with no land in sight, wayfinders tracked their position mentally — a technique sometimes called "dead reckoning" — constantly updating their sense of distance and direction from speed, heading, and time elapsed.' },
  { count: 14, key: 'compass_stars',     label: 'Following the compass stars',  teReo: 'Te whai i ngā whetū kaiārahi',      scene: 4,
    note: 'A "star compass" is a mental map of the horizon, divided into houses where key stars rise and set. Navigators memorised dozens of these positions to hold a steady heading through the night.' },
  { count: 22, key: 'weathering_swell',  label: 'Weathering a swell',           teReo: 'Te whakaeke i te ngaru',            scene: 5,
    note: 'Experienced wayfinders could read the direction and pattern of ocean swells passing beneath the hull — even lying down to feel the movement — to hold a course when clouds hid the stars.' },
  { count: 30, key: 'landfall_sighted',  label: 'Landfall sighted',             teReo: 'Kua kitea te whenua',               scene: 6,
    note: 'Land can be sensed well before it\'s visible — from clouds that build differently over islands, changes in swell patterns, and land-based birds flying further out to sea in the morning and back at dusk.' },
  { count: 40, key: 'arrival',           label: 'Arrival — full scene',         teReo: 'Kua ū ki uta',                      scene: 7,
    note: 'This style of navigation — reading stars, swells, wind, and wildlife together, with no instruments — is called wayfinding. It\'s widely regarded as one of the great feats of exploration in human history.' },
];

const MILESTONES_Y7_T2 = [
  { count: 1,  key: 'dawn_breaks',       label: 'Dawn breaks over the voyage',  teReo: 'Ka mārama te ata',                  scene: 1 },
  { count: 3,  key: 'reading_current',   label: 'Reading the current',          teReo: 'Te tātari i te au',                 scene: 2 },
  { count: 7,  key: 'sharing_the_hold',  label: 'Sharing the hold fairly',      teReo: 'Te tuwha ōrite',                    scene: 3 },
  { count: 14, key: 'weighing_the_odds', label: 'Weighing the odds',            teReo: 'Te whakaaro ki ngā tūponotanga',    scene: 4 },
  { count: 22, key: 'course_correction', label: 'Course correction',            teReo: 'Te whakatika ara',                  scene: 5 },
  { count: 30, key: 'land_sighted',      label: 'Land sighted',                 teReo: 'Kua kitea te whenua',               scene: 6 },
  { count: 40, key: 'landfall',          label: 'Landfall — full scene',        teReo: 'Kua ū ki uta',                      scene: 7 },
];

// Year 5 themes, keyed by term (1-4). Kept as its own export name for
// backward compatibility with anything still importing TERM_THEMES directly.
export const TERM_THEMES = {
  1: {
    term: 1,
    available: true,
    name: 'Native Garden',
    teReo: 'Te māra taketake o Aotearoa',
    description: 'Grow a traditional Aotearoa native garden — harakeke, kōwhai, pōhutukawa and the manu that love them.',
    assetPrefix: '/garden/scene',
    accent: 'fern',
    emptyCopy: 'Complete your first Term 1 lesson to plant a harakeke shoot.',
    tagline: 'Te māra taketake o Aotearoa',
    shareSubtitle: 'Te māra taketake o Aotearoa',
    milestones: MILESTONES_T1,
  },
  2: {
    term: 2,
    available: true,
    name: 'Matariki Night Sky',
    teReo: 'Te whānau Matariki',
    description: "Seven stars of Matariki rise one-by-one as you learn. Each star carries a tikanga — from food-from-soil to the winds themselves.",
    assetPrefix: '/matariki/scene',
    accent: 'moana',
    emptyCopy: 'Complete your first Term 2 lesson and Matariki, the mother star, will rise.',
    tagline: 'Te whānau Matariki',
    shareSubtitle: 'Te whānau Matariki e piataata ana',
    milestones: MILESTONES_T2,
  },
  3: {
    term: 3,
    available: true,
    name: 'Kaimoana Rockpool',
    teReo: 'Te kōhatu wai o te moana',
    description: 'A barren coastal rockpool fills with native sea life — pātangaroa, kina, pāua, wheke — as you steward your learning.',
    assetPrefix: '/rockpool/scene',
    accent: 'moana',
    emptyCopy: 'Complete your first Term 3 lesson and a pātangaroa (starfish) will appear.',
    tagline: 'Ki a kaitiakitanga o te moana',
    shareSubtitle: 'Te kōhatu wai e puta ana',
    milestones: MILESTONES_T3,
  },
  4: {
    term: 4,
    available: true,
    name: 'Summer Beach & Pōhutukawa',
    teReo: 'Te tātahi o te raumati',
    description: 'A Kiwi summer beach comes alive — driftwood, pōhutukawa in full red bloom, whānau on the sand, terns wheeling above.',
    assetPrefix: '/beach/scene',
    accent: 'pohutukawa',
    emptyCopy: 'Complete your first Term 4 lesson and the first shell will wash ashore.',
    tagline: 'Te tātahi o te raumati',
    shareSubtitle: 'Kua tae mai te raumati',
    milestones: MILESTONES_T4,
  },
};

// Year 6 themes — a single "journey" narrative across the year (mountain
// ascent -> river -> night forest -> waka voyage), distinct from Year 5's
// static seasonal scenes. Only Term 1 has art so far; Terms 2-4 show the
// "coming soon" preview card until their assets ship.
const YEAR6_THEMES = {
  1: {
    term: 1,
    available: true,
    name: 'Maunga Ascent',
    teReo: 'Te Piki i te Maunga',
    description: 'Climb a native mountain trail from the beech forest to the alpine summit — kea, tussock, and mountain daisies mark the way.',
    assetPrefix: '/maunga/scene',
    accent: 'pohutukawa',
    emptyCopy: 'Complete your first Term 1 lesson to step onto the trail.',
    tagline: 'Te Piki i te Maunga',
    shareSubtitle: 'Te Piki i te Maunga',
    milestones: MILESTONES_Y6_T1,
  },
  2: {
    term: 2,
    available: false,
    name: 'Awa Journey',
    teReo: 'Te Haerenga o te Awa',
    description: "Follow the awa from its mountain spring all the way to the sea — kōura, tuna, and native birds mark the journey downstream.",
  },
  3: {
    term: 3,
    available: false,
    name: 'Ngahere by Night',
    teReo: 'Te Ngahere i te Pō',
    description: 'A night walk through native forest by starlight — glowing titiwai, a calling ruru, and a kiwi rustling in the undergrowth.',
  },
  4: {
    term: 4,
    available: false,
    name: 'Waka Voyage',
    teReo: 'Te Haerenga Waka',
    description: 'Set sail on a waka hourua and navigate by the stars to a new island — the final leg of the year, guided by Matariki.',
  },
};

// Year 7 themes — a step further than Year 6's guided mountain trail: the
// child now navigates open ocean by starlight rather than following a
// marked path. Only Term 1 has curriculum so far (see
// docs/curriculum/year7-plan.md) — Terms 2-4 are placeholders until their
// curriculum is planned, matching Year 6's own Terms 2-4 treatment.
const YEAR7_THEMES = {
  1: {
    term: 1,
    available: true,
    name: 'Wayfinder Voyage',
    teReo: 'Te Haerenga Kaiwhakatere',
    description: 'Sail a waka hourua across open ocean at night, navigating by Matariki and the stars — no marked trail, just the sky and your own judgement.',
    assetPrefix: '/wayfinder/scene',
    accent: 'kowhai',
    emptyCopy: 'Complete your first Term 1 lesson to cast off from shore.',
    tagline: 'Te Haerenga Kaiwhakatere',
    shareSubtitle: 'Kua takoto te ara o ngā whetū',
    milestones: MILESTONES_Y7_T1,
  },
  2: {
    // Designed (see docs/superpowers/specs/2026-08-17-year7-term2-theme-design.md)
    // but not shipped — no art assets exist yet, so `available` stays false
    // and no `assetPrefix` is set. Flip `available: true` and add
    // `assetPrefix: '/starcompass/scene'` once the 8 illustration frames
    // from that spec are produced.
    term: 2,
    available: false,
    name: 'Star Compass Landfall',
    teReo: 'Te Ūnga ā-Kāpehu Whetū',
    description: 'Dawn breaks over open water. Read the current, share the crew\'s supplies fairly, and correct your course star by star until land is finally sighted.',
    accent: 'moana',
    emptyCopy: 'Complete your first Term 2 lesson to watch dawn break over the voyage.',
    tagline: 'Te Ūnga ā-Kāpehu Whetū',
    shareSubtitle: 'Kua kitea te whenua',
    milestones: MILESTONES_Y7_T2,
  },
  3: {
    term: 3,
    available: false,
    name: 'Term 3 theme coming soon',
    teReo: '',
    description: 'This term\'s theme will be designed once Year 7 Term 3\'s curriculum is planned.',
  },
  4: {
    term: 4,
    available: false,
    name: 'Term 4 theme coming soon',
    teReo: '',
    description: 'This term\'s theme will be designed once Year 7 Term 4\'s curriculum is planned.',
  },
};

const YEAR_THEMES = {
  5: TERM_THEMES,
  6: YEAR6_THEMES,
  7: YEAR7_THEMES,
};

/** Pick the highest scene the learner has unlocked (0 if none). */
export function sceneForActiveDays(theme, activeDays) {
  let scene = 0;
  for (const m of theme?.milestones || []) {
    if (activeDays >= m.count) scene = m.scene;
  }
  return scene;
}

/**
 * Safe getter — falls back to Year 5 Term 1 if an unknown year/term is requested.
 *
 * Themes whose art has not shipped yet are declared with `available: false` and
 * OMIT `milestones` entirely. Callers iterate and filter that array without
 * checking, so returning such a theme raw threw
 * "Cannot read properties of undefined (reading 'filter')" and blanked the whole
 * Dashboard — which is exactly what happened to Year 6 and Year 7 families once
 * the school year reached a term whose scene art does not exist yet.
 *
 * Returning "a theme object" was never enough; it has to be a COMPLETE one.
 */
export function getTheme(year, termNumber) {
  const themesForYear = YEAR_THEMES[year] || YEAR_THEMES[5];
  const theme = themesForYear[termNumber] || themesForYear[1] || TERM_THEMES[1];
  return Array.isArray(theme.milestones) ? theme : { ...theme, milestones: [] };
}
